import type { SciChartSurface } from 'scichart';
import type { ElementDescriptor, SciChartElementHooks } from './types';

// SciChart export hooks: scale fonts/markers up for the larger export and toggle
// Axis Titles/Labels, all via SciChart's public API; restore* reverts each apply*.

// Drawer keys — kept stable so `options.elements` round-trips.
const AXIS_TITLES = 'axisTitles';
const AXIS_LABELS = 'axisLabels';

// Base font size (px, at 1×) for an export-only axis title; scaled by fontFactor.
const EXPORT_AXIS_TITLE_FONT_SIZE = 12;

// An axis title to render only in the export (never on the live chart), keyed by
// the axis id. Applied during capture and reverted immediately afterwards.
export interface ExportAxisTitle {
  axisId: string;
  title: string;
}

export interface SciChartElementHookOptions {
  // Titles injected onto axes for the export only. Read at export time so the
  // value can track live state (e.g. the selected x-axis dimension).
  getExportAxisTitles?: () => ExportAxisTitle[];
}

// Structural views over the SciChart objects — we only touch the members we
// scale/toggle, so we avoid importing the concrete classes.
interface ScalableTextStyle {
  fontSize?: number;
  [key: string]: unknown;
}
interface ChartAxis {
  labelStyle: ScalableTextStyle;
  axisTitleStyle: ScalableTextStyle;
  drawLabels: boolean;
  axisTitle?: string | string[];
  maxAutoTicks?: number;
}

// Absolute-px ceilings so a large userFactor can't blob the markers/lines —
// clamps the result, not the multiplier (mirrors Highcharts' radius/lineWidth).
const MARKER_MAX_PX = 32;
const STROKE_MAX_PX = 12;

// Per-size axis-tick ceiling so enlarged labels don't crowd the plot (XL sparser
// than Large); thresholds bracket the shared L 2.0 / XL 2.5 multipliers.
const reducedMaxTicksFor = (userFactor: number): number | null => {
  if (userFactor >= 2.5) return 3; // XL
  if (userFactor >= 2) return 5; // Large
  return null;
};
interface ScalablePointMarker {
  width: number;
  height: number;
}
interface ScalableSeries {
  strokeThickness: number;
  pointMarker?: ScalablePointMarker;
}

const hasAxisTitle = (axis: ChartAxis): boolean => {
  const t = axis.axisTitle;
  if (Array.isArray(t)) return t.some((s) => s.trim().length > 0);
  return typeof t === 'string' && t.trim().length > 0;
};

// SciChart's `renderableSeries` is polymorphic: each entry is either a leaf series
// or a COLLECTION of series (a stacked column/mountain group). A collection extends
// `ObservableArray`, so — exactly like `renderableSeries` itself — it exposes
// `asArray()`; a leaf series does not. Flattening to leaves means scaling touches the
// child series that actually own strokeThickness/pointMarker, and never the collection
// wrapper (whose getters throw). Recursive, so it handles any depth and any current or
// future collection type without a per-type branch.
function flattenSeries(entries: unknown[]): ScalableSeries[] {
  const leaves: ScalableSeries[] = [];
  for (const entry of entries) {
    const asArray = (entry as { asArray?: () => unknown[] }).asArray;
    if (typeof asArray === 'function') {
      leaves.push(...flattenSeries(asArray.call(entry)));
    } else {
      leaves.push(entry as ScalableSeries);
    }
  }
  return leaves;
}

// A leaf may simply not expose a given optional property (a column has no
// pointMarker; some series types throw on an unsupported getter). Read it
// defensively so we scale what a series supports and skip the rest — never
// assuming every series type has every property.
function safeRead<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

export function createSciChartElementHooks(
  getSurface: () => SciChartSurface | null,
  options?: SciChartElementHookOptions
): SciChartElementHooks {
  // Each apply* pushes its inverse here; drained (LIFO) by the matching restore*.
  let scaleUndo: Array<() => void> = [];
  let visibilityUndo: Array<() => void> = [];

  const axesOf = (surface: SciChartSurface): ChartAxis[] =>
    [
      ...surface.xAxes.asArray(),
      ...surface.yAxes.asArray(),
    ] as unknown as ChartAxis[];

  const findAxisById = (
    surface: SciChartSurface,
    axisId: string
  ): ChartAxis | undefined => {
    const axis = surface.xAxes.getById(axisId) ?? surface.yAxes.getById(axisId);
    return axis as unknown as ChartAxis | undefined;
  };

  return {
    // Only the elements this surface actually renders on-canvas — dynamic per
    // chart so the drawer never shows a toggle that would do nothing.
    getElements: (): ElementDescriptor[] => {
      const surface = getSurface();
      if (!surface) return [];
      const axes = axesOf(surface);
      const descriptors: ElementDescriptor[] = [];
      if (axes.some((a) => hasAxisTitle(a)))
        descriptors.push({
          key: AXIS_TITLES,
          label: 'Axis Titles',
          available: true,
        });
      if (axes.some((a) => a.drawLabels !== false))
        descriptors.push({
          key: AXIS_LABELS,
          label: 'Axis Labels',
          available: true,
        });
      return descriptors;
    },

    applyScaling: ({ fontFactor, userFactor }) => {
      const surface = getSurface();
      if (!surface) return;
      scaleUndo = [];

      const scaleFont = (
        read: () => ScalableTextStyle,
        write: (style: ScalableTextStyle) => void
      ) => {
        const original = read();
        if (!original || typeof original.fontSize !== 'number') return;
        write({ ...original, fontSize: original.fontSize * fontFactor });
        scaleUndo.push(() => write(original));
      };

      const tickCeiling = reducedMaxTicksFor(userFactor);

      const axes = axesOf(surface);
      for (const axis of axes) {
        scaleFont(
          () => axis.labelStyle,
          (style) => {
            axis.labelStyle = style;
          }
        );
        scaleFont(
          () => axis.axisTitleStyle,
          (style) => {
            axis.axisTitleStyle = style;
          }
        );

        // Thin ticks per size so the enlarged labels don't crowd the plot.
        if (
          tickCeiling !== null &&
          typeof axis.maxAutoTicks === 'number' &&
          axis.maxAutoTicks > tickCeiling
        ) {
          const originalMax = axis.maxAutoTicks;
          axis.maxAutoTicks = tickCeiling;
          scaleUndo.push(() => {
            axis.maxAutoTicks = originalMax;
          });
        }
      }

      // Flatten collections (stacked groups) to their leaf series so we scale the
      // series that actually own these props, not the collection wrapper.
      const series = flattenSeries(surface.renderableSeries.asArray());
      for (const rs of series) {
        const stroke = safeRead(() => rs.strokeThickness);
        // Preserve an explicit 0 (used to hide a stroke).
        if (typeof stroke === 'number' && stroke > 0) {
          rs.strokeThickness = Math.min(stroke * userFactor, STROKE_MAX_PX);
          scaleUndo.push(() => {
            rs.strokeThickness = stroke;
          });
        }
        const marker = safeRead(() => rs.pointMarker);
        if (marker && marker.width > 0 && marker.height > 0) {
          const { width, height } = marker;
          marker.width = Math.min(width * userFactor, MARKER_MAX_PX);
          marker.height = Math.min(height * userFactor, MARKER_MAX_PX);
          scaleUndo.push(() => {
            marker.width = width;
            marker.height = height;
          });
        }
      }

      // Inject export-only axis titles (never on the live chart), scaled and
      // reverted with the rest of the scaling; an Axis Titles=off toggle hides them.
      for (const { axisId, title } of options?.getExportAxisTitles?.() ?? []) {
        if (!title.trim()) continue;
        const axis = findAxisById(surface, axisId);
        if (!axis) continue;
        const originalTitle = axis.axisTitle;
        const originalTitleStyle = axis.axisTitleStyle;
        axis.axisTitle = title;
        axis.axisTitleStyle = {
          ...originalTitleStyle,
          fontSize: EXPORT_AXIS_TITLE_FONT_SIZE * fontFactor,
        };
        scaleUndo.push(() => {
          axis.axisTitle = originalTitle;
          axis.axisTitleStyle = originalTitleStyle;
        });
      }
    },

    restoreScaling: () => {
      for (let i = scaleUndo.length - 1; i >= 0; i -= 1) scaleUndo[i]();
      scaleUndo = [];
    },

    // `elements`: absent key = include; only `false` hides. We only act on the
    // keys we advertised in getElements.
    applyVisibility: (elements) => {
      const surface = getSurface();
      if (!surface) return;
      visibilityUndo = [];
      const axes = axesOf(surface);

      if (elements[AXIS_LABELS] === false) {
        for (const axis of axes) {
          if (axis.drawLabels !== false) {
            axis.drawLabels = false;
            visibilityUndo.push(() => {
              axis.drawLabels = true;
            });
          }
        }
      }

      if (elements[AXIS_TITLES] === false) {
        for (const axis of axes) {
          if (hasAxisTitle(axis)) {
            const original = axis.axisTitle;
            axis.axisTitle = '';
            visibilityUndo.push(() => {
              axis.axisTitle = original;
            });
          }
        }
      }
    },

    restoreVisibility: () => {
      for (let i = visibilityUndo.length - 1; i >= 0; i -= 1)
        visibilityUndo[i]();
      visibilityUndo = [];
    },
  };
}
